import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { loadTokens, loadTokensFailed, loadTokensSuccess } from '../actions/tokens.actions';
import { WorkspaceService } from 'src/app/services/workspace/workspace.service';
import { WorkspaceLicensesService } from 'src/app/services/workspace-licenses/workspace-licenses.service';
import { TOKEN_PACKAGE_SKU_PREFIX, TOKEN_ACTION_SKU_MAP, TokenBalance } from 'src/app/services/licenses/licenses.model';
import { WorkspaceLicense } from 'src/app/models/user-licenses.model';

@Injectable()
export class TokensEffects {
    private actions$ = inject(Actions);
    private workspaceService = inject(WorkspaceService);
    private workspaceLicensesService = inject(WorkspaceLicensesService);


    /**
     * Load tokens effect - builds TokenBalance from workspace licenses.
     *
     * Flow:
     * 1. Get all workspace licenses
     * 2. Find the token package license (PACK-TOKENS-*)
     * 3. If no token license exists, fail with 'no-token-license'
     * 4. Build TokenBalance from package license and TOKN-* action licenses
     */
    loadTokens$ = createEffect(() => this.actions$.pipe(
        ofType(loadTokens),
        switchMap(() => {
            return from(this.workspaceLicensesService.getWorkspaceLicenses()).pipe(
                map((allLicenses: WorkspaceLicense[]) => {
                    // Find the token package license (PACK-TOKENS-*)
                    const tokenPackageLicense = allLicenses.find(l => l.sku?.startsWith(TOKEN_PACKAGE_SKU_PREFIX));

                    // If no token package license exists, tokens are not available
                    if (!tokenPackageLicense) {
                        return loadTokensFailed({ error: 'no-token-license' });
                    }

                    // Build token balance from licenses
                    const tokenBalance = this.buildTokenBalanceFromLicenses(tokenPackageLicense, allLicenses);
                    return loadTokensSuccess({ tokenBalance });
                }),
                catchError((err) => of(loadTokensFailed({ error: err })))
            );
        })
    ));

    /**
     * Build a TokenBalance object from workspace licenses.
     * - Token balance comes from the PACK-TOKENS-* license (totalAmount/remainingAmount)
     * - Per-action execution limits come from TOKN-* licenses in the workspace
     */
    private buildTokenBalanceFromLicenses(tokenPackageLicense: WorkspaceLicense, allLicenses: WorkspaceLicense[]): TokenBalance {
        const executionsRemaining: { [key: string]: number | null } = {};

        // Map TOKN-* licenses to action types and extract their remaining amounts
        for (const license of allLicenses) {
            if (!license.sku) continue;

            // Find matching action type by SKU prefix
            for (const [skuPrefix, actionType] of Object.entries(TOKEN_ACTION_SKU_MAP)) {
                if (license.sku.startsWith(skuPrefix)) {
                    executionsRemaining[actionType] = license.remainingAmount ?? null;
                    break;
                }
            }
        }

        return {
            currentTokenBalance: tokenPackageLicense.remainingAmount ?? 0,
            monthlyToken: tokenPackageLicense.totalAmount ?? 0,
            executionsRemaining
        };
    }
}
